<template>
  <div class="edit-document">
    <transition name="dialogfade">
      <k-dialog v-if="showDialog"
                class="edit-condition-dialog edit-document-dialog"
                :title="document?.title"
                @close="closeDialog">
        <div class="edit-condition__wrapper">
          <div class="edit-condition__detail-tracking">
            <section class="edit-condition__section-detail">
              <h2 class="section--title title--document-detail">Document Detail</h2>

              <div class="ai-summary-label" v-if="selectedAttachment.id && selectedAttachment.id !== 'All' && (isValidBucketForSummary || showChatResponse || documentChatResponse || isChatLoading)">
                <div class="ai-summary-header">
                  <span class="ai-summary-title">
                    <span v-if="!showChatResponse">AI Summary ✨</span>
                    <span v-else>Chat Response 💬</span>
                  </span>
                  <div class="ai-summary-controls">
                    <!-- Back to Summary button when showing chat response -->
                    <k-button v-if="showChatResponse"
                              @click="backToSummary"
                              class="back-to-summary-btn"
                              title="Back">
                      <span class="k-icon k-i-arrow-left"></span>
                      <span class="btn-text">Back</span>
                    </k-button>

                    <!-- Show Regenerate button when there's existing content and not showing chat -->
                    <k-button v-if="!showChatResponse && editedDocument.aidescription && !isAISummaryLoading"
                              @click="regenerateAISummary"
                              :disabled="isRegeneratingAI"
                              class="regenerate-btn"
                              title="Regenerate AI Summary">
                      <span v-if="!isRegeneratingAI" class="k-icon k-i-refresh"></span>
                      <span v-else class="k-icon k-i-loading"></span>
                      <span class="btn-text">{{ isRegeneratingAI ? 'Loading...' : 'Regenerate' }}</span>
                    </k-button>

                    <!-- Show Generate button when there's no content and not showing chat -->
                    <k-button v-if="!showChatResponse && !editedDocument.aidescription && !isAISummaryLoading"
                              @click="generateAISummary"
                              :disabled="isRegeneratingAI"
                              class="regenerate-btn"
                              title="Generate AI Summary">
                      <span v-if="!isRegeneratingAI" class="k-icon k-i-refresh"></span>
                      <span v-else class="k-icon k-i-loading"></span>
                      <span class="btn-text">{{ isRegeneratingAI ? 'Loading...' : 'Generate Summary' }}</span>
                    </k-button>
                  </div>
                </div>

                <!-- Show content area when there's content or loading -->
                <div v-if="(!showChatResponse && (editedDocument.aidescription || isAISummaryLoading || isRegeneratingAI)) || (showChatResponse && (documentChatResponse || isChatLoading))"
                     class="ai-summary-content"
                     :class="{ 'loading': isAISummaryLoading || isRegeneratingAI || (isChatLoading && !documentChatResponse) }"
                     @click="onDataPointClick"
                     v-html="showChatResponse ? formatChatMessage(documentChatResponse) : formattedAISummary">
                </div>

                <!-- Error Display -->
                <div v-if="chatError" class="chat-error">
                  <span class="k-icon k-i-warning"></span>
                  {{ chatError }}
                </div>

                <small v-if="isAISummaryLoading && !showChatResponse"
                       style="display: block; margin-top: 4px; color: #666">
                  Loading AI Summary...
                </small>

                <small v-if="isRegeneratingAI && !showChatResponse"
                       style="display: block; margin-top: 4px; color: #666">
                  Generating AI Summary...
                </small>

                <!--<small v-if="isChatLoading && showChatResponse"
                       style="display: block; margin-top: 4px; color: #666">
                  AI is responding...
                </small>-->

                <!-- Appraisal Comparable Distance Notice -->
                <div v-if="shouldShowAppraisalDistanceNotice" class="appraisal-distance-notice">
                  <span class="k-icon k-i-warning"></span>
                  <span>Review Required - Comparable Further than One Mile. </span>
                </div>

                <!-- Appraisal Condition Notice -->
                <div v-if="shouldShowAppraisalConditionNotice" class="appraisal-condition-notice">
                  <span class="k-icon k-i-warning"></span>
                  <span>Review Required - Condition Rating C5 or Above. </span>
                </div>

                <!-- Appraisal Adjustment Notice -->
                <div v-if="shouldShowAppraisalAdjustmentNotice" class="appraisal-adjustment-notice">
                  <span class="k-icon k-i-warning"></span>
                  <span>Review Required - Net Adjustment Over 10% or Gross Adjustment Over 15%. </span>
                </div>
              </div>

              <!-- Document Chat Input -->
              <div class="document-chat-container" v-if="selectedAttachment.id && selectedAttachment.id !== 'All'">
                <div class="chat-input-wrapper">
                  <k-textarea class="document-chat-input"
                              :placeholder="chatInputPlaceholder"
                              :rows="2"
                              v-model="documentChatInput"
                              @keydown="onDocumentChatInput"
                              :disabled="isChatLoading"></k-textarea>
                  <div class="chat-input-icon">
                    <span v-if="isChatLoading" class="k-icon k-i-loading chat-loader"></span>
                    <span v-else class="ai-icon">✨</span>
                  </div>
                </div>
              </div>

              <label>
                Description
                <k-textarea @input="checkForDocumentChanges" :rows="5" v-model="editedDocument.description"></k-textarea>
              </label>

              <label class="current-conditions__label">
                Current Conditions
                <k-input placeholder="Search by title..."
                         :value="condSearchWord"
                         :icon-name="'search'"
                         @input="onSearchConditions"></k-input>
              </label>
              <div class="document-condition__container">
                <div class="document-condition__row" v-for="condition in filteredCurrentConditionList" :key="condition.entityId">
                  <p class="document-condition__item">
                    <span class="document-condition__item--name">{{ condition.entityName }}</span>
                    <span title="Remove Condition" class="document-condition__item--remove k-icon k-i-close .k-i-x" v-on:click="onRemoveCondition(condition)"></span>
                  </p>
                </div>
              </div>

              <!-- Add Conditions section moved inside main section -->
              <div class="select-condition__wrapper">
                <h2 class="section--title">Add Condition(s)</h2>
                <div class="select-condition__dropdown">
                  <k-multiselect :data-items="filteredConditionList"
                                 :placeholder="'Select Condition'"
                                 :text-field="'title'"
                                 :data-item-key="'id'"
                                 v-model="selectedConditionValues"
                                 :filterable="true"
                                 @filterchange="onConditionFilterChange"
                                 @change="onConditionSelected"></k-multiselect>
                </div>
              </div>

              <div class="document-comments__container">
                    <h2 class="section--title">Comments</h2>
                    <div class="document-comments" v-if="documentComments?.length > 0">
                        <div class="document-comment" v-for="comment in documentComments" :id="comment.id">
                            <p class="comment-info">{{ comment.addedDate.toLocaleDateString() }} {{comment.addedDate.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit'}).split(',').join('') }}
                                <span class="comment-user">{{ comment.addedBy?.entityId }}</span>
                            </p>
                            <p class="comment-text">{{ comment.comments }}</p>
                        </div>
                    </div>
                    <p class="no-comments" v-if="documentComments?.length === 0 && !documentCommentsLoading">No comments available</p>
                    <div class="add-comment">
                        <k-textarea :rows="5" :auto-size="true" v-model="newCommentText" :placeholder="'Enter your comment here'"></k-textarea>
                        <k-button class="add-comment-btn" :disabled="!newCommentText" @click="onCreateComment"><i class="fa-solid fa-message-plus"></i> Add Comment</k-button>
                    </div>
              </div>
            </section>
          </div>
          <div class="edit-condition__attachments">
              <div class="attachments-grid__panel-container">
                  <k-panelbar :items="panelItems" class="attachments-grid__panel">
                      <template v-slot:attachmentsgrid>
                          <div class="edit-condition__assign-doc">
                              <span class="document--info"><span class="k-icon k-i-information k-i-info"></span> Select a file To View</span>
                              <k-button @click="onViewAllFiles" class="assign-doc__btn"><span class="k-icon k-i-file .k-i-file-vertical"></span> View All</k-button>
                          </div>
                          <k-grid :data-items="documentAttachments"
                              :columns="attachmentColumns"
                              :sortable="sortable"
                              :sort="attachmentSort"
                              :selected-field="selectedField"
                              :data-item-key="'id'"
                              @sortchange="attachmentSortChangeHandler"
                              @rowclick="onOpenAttachment">
                              <template v-slot:currentVersion="{props}">
                                    <td class="attachment__version-cell">
                                        <span><k-checkbox v-model="props.dataItem.isActive" @change="onCurrentVersionChanged(props.dataItem)"></k-checkbox></span>
                                    </td>
                              </template>
                              <template v-slot:junkAttachment="{props}">
                                  <div class="junk-attachment-col">
                                      <k-button title="Move to junk folder" v-on:click.stop="onJunkAttachment(props.dataItem)"><span class="k-icon k-i-delete k-i-trash"></span></k-button>
                                  </div>
                              </template>
                          </k-grid>
                      </template>
                  </k-panelbar>
              </div>
            <div class="viewer__container">
              <WebViewer :showEditorButton="true" 
                            @removeAttchment="RemoveAttachments" 
                            @fileSplit="fileSplitStarted" 
                            @loaded="onWebViewerLoaded"
                            :attachments="documentAttachments" 
                            :document="document" 
                            :selectedAttchement="selectedAttachment" 
                            :pdfFile="selectedAttachment.url" 
                            ref="webViewer"
                            v-if="!isAttachmentLoading && selectedAttachment.url" />
              <div class="document-attachment__loader-container" v-if="isAttachmentLoading">
                <loader :size="'large'" :type="'pulsing'"></loader>
              </div>
            </div>
          </div>
        </div>
        <k-dialog-actions>
          <k-button v-bind:class="['dialog__btn dialog__btn--save', { 'dialog__btn--save-active': !saveDocumentBtnDisabled }]" v-on:click="saveDocument">{{ saveDocumentBtnDisabled ? 'Close' : 'Save and Close' }}</k-button>
        </k-dialog-actions>

        <div class="conditions__loader-container" v-if="isLoading">
          <loader :size="'large'" :type="'infinite-spinner'"></loader>
        </div>
      </k-dialog>
    </transition>

    <transition name="dialogfade">
      <k-dialog v-if="showConfirmDialog"
                class="edit-condition-dialog confirm-junk-dialog"
                :title="saveChangesWarning ? 'You Have Unsaved Changes' : 'Please Confirm'"
                @close="toggleConfirmDialog">
        <div v-if="confirmJunkAttachment">
          <p v-if="junkFolderId && !saveChangesWarning" class="confirm-junk--msg">Are you sure you would like to move <span>{{ selectedJunkedAttachment.title }}</span> to the junk folder?</p>
          <p v-if="!junkFolderId && !saveChangesWarning" class="confirm-junk--msg confirm-junk--msg-error">Loan file junk folder not found</p>
          <p class="save-changes-warning" v-if="saveChangesWarning"><span class="k-icon k-i-warning k-i-exception k-i-error"></span>Please save your changes before continuing</p>
        </div>
        <div v-if="confirmRemoveCondition">
          <p v-if="!saveChangesWarning" class="confirm-junk--msg">Are you sure you would like to remove <span>{{ selectedRemovedCondition.entityName }}?</span></p>
          <p class="save-changes-warning" v-if="saveChangesWarning"><span class="k-icon k-i-warning k-i-exception k-i-error"></span>Please save your changes before continuing</p>
        </div>
        <k-dialog-actions v-if="confirmJunkAttachment && !saveChangesWarning">
          <k-button class="dialog__btn dialog__btn--close" v-on:click="cancelJunkAttachment">{{ junkFolderId ? 'No' : 'Close' }}</k-button>
          <k-button class="dialog__btn dialog__btn--save" :disabled="!junkFolderId" v-on:click="junkAttachment">Yes</k-button>
        </k-dialog-actions>
        <k-dialog-actions v-if="confirmRemoveCondition && !saveChangesWarning">
          <k-button class="dialog__btn dialog__btn--close" v-on:click="cancelRemoveCondition">No</k-button>
          <k-button class="dialog__btn dialog__btn--save" v-on:click="removeCondition">Yes</k-button>
        </k-dialog-actions>
        <k-dialog-actions v-if="saveChangesWarning">
          <k-button class="dialog__btn dialog__btn--close" v-on:click="closeWarningDialog">Close</k-button>
        </k-dialog-actions>
      </k-dialog>
    </transition>

    <transition name="dialogfade">
      <k-dialog v-if="showConfirmCloseDialog"
                class="edit-condition-dialog confirm-junk-dialog"
                :title="'You have unsaved changes'"
                @close="toggleConfirmCloseDialog">
        <p class="confirm-junk--msg">Are you sure you would like to cancel your changes?</p>
        <k-dialog-actions>
          <k-button class="dialog__btn dialog__btn--close" v-on:click="toggleConfirmCloseDialog">No</k-button>
          <k-button class="dialog__btn dialog__btn--save" v-on:click="confirmCloseDialog">Yes</k-button>
        </k-dialog-actions>
      </k-dialog>
    </transition>

    <k-notification-group class="conditions__notification-container">
      <k-fade :appear="successNotification">
        <k-notification v-if="successNotification"
                        :type="{ style: 'success', icon: true }"
                        :closable="true"
                        @close="onCloseNotification('successNotification')">
          <span>{{ notificationMsg }}</span>
        </k-notification>
      </k-fade>
    </k-notification-group>

    <transition name="dialogfade">
      <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
        <p class="error-dialog__msg">{{ errorMsg }}</p>
      </k-dialog>
    </transition>

    <transition name="dialogfade">
      <k-dialog v-if="showFileSplitDialog"
                class="edit-condition-dialog confirm-split-dialog"
                :title-render="customTitleBar"
                @close="toggleFileSplitDialog">
        <template v-slot:customTitleBar="{props, listeners}">
          <div class="custom-title" >
            <i  class="fa-duotone fa-arrows-split-up-and-left"></i><span>Split File</span>
          </div>
        </template>
        <div class="file-info-wrapper">
          <div class="document-title">
            <k-dropdown :data-items="documentList"
                        :label="'Document Bucket'"
                        :text-field="'title'"
                        :data-item-key="'id'"
                        v-model="selectedDocumentValue"
                        @change="documentTitleChanged"
                        :filterable="false"></k-dropdown>
          </div>
          <div class="file-name">
            <k-input class="file-name-input" v-model="fileTitle" :label="'File Name'"></k-input>
          </div>
        </div>
        <k-dialog-actions>
          <k-button class="dialog__btn dialog__btn--close" v-on:click="toggleFileSplitDialog">Cancel</k-button>
          <k-button class="dialog__btn dialog__btn--save" v-on:click="confirmFileSplitDialog">Save Split File</k-button>
        </k-dialog-actions>
      </k-dialog>
    </transition>
  </div>
</template>

<script>
    import auth from '../authConfig.js';
    import apiService from '../api/api.service.js';

    import { MultiSelect, DropDownList } from '@progress/kendo-vue-dropdowns';
    import { Dialog, DialogActionsBar, Window } from '@progress/kendo-vue-dialogs';
    import { Input, TextArea, Checkbox } from "@progress/kendo-vue-inputs";
    import { Button } from '@progress/kendo-vue-buttons';
    import { Loader } from "@progress/kendo-vue-indicators";
    import { Grid, GridToolbar } from '@progress/kendo-vue-grid';
    import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
    import { Fade, Reveal, Slide } from "@progress/kendo-vue-animation";
    import { PanelBar } from '@progress/kendo-vue-layout';

    import { filterBy } from '@progress/kendo-data-query';
    import { orderBy } from '@progress/kendo-data-query';

    import cloneDeep from 'lodash.clonedeep';
    import isEqual from 'lodash.isequal';
    import WebViewer from "../components/WebViewer.vue";

    export default {
    props: ['document', 'junkFolderId', 'loanConditions', 'loanId', 'showDialog','documents'],
        emits: ['closenotification', 'documentupdated', 'dialogclosed', 'requesterror', 'shownotification', 'updatedocument', 'updatedocumentconditions'],
        data() {
          return {
                attachmentColumns: [
                    { field: 'title', title: 'Title' },
                    { field: 'createdBy.entityId', title: 'Added By' },
                    { field: 'createdDate', title: 'Date Added', filter: "date", type: "date", format: "{0:MM/dd/yyyy}" },
                    { cell: 'currentVersion', field: 'isActive', title: 'Current Version', width: '150px', columnMenu: false, filterable: false },
                    { cell: 'junkAttachment', width: '75px', columnMenu: false, filterable: false }
                ],
                attachmentSort: [],
                chatError: null,
                conditionList: [],
                conditionsAdded: false,
                condSearchWord: '',
                confirmJunkAttachment: false,
                confirmRemoveCondition: false,
                customTitleBar:'customTitleBar',
                documentAttachments: [],
                documentChatInput: '',
                documentChatMessages: [],
                documentChatResponse: '',
                documentComments: [],
                documentCommentsLoading: false,
                documentUpdated: false,
                editedDocument: {},
                fileTitle:'',
                filteredConditionList: [],
                filteredCurrentConditionList: [],
                isAISummaryLoading: false,
                isChatLoading: false,
                aiSummaryFetched: false,
                isAttachmentLoading: false,
                isLoading: false,
                errorAlert: false,
                errorMsg: '',
                errorTitle: '',
                notificationMsg: '',
                validatedDataPoints: new Set(),
                isValidatingDataPoints: false,
                dataPointSearchState: {}, // Track search state for each data point
                lastClickedDataPointId: null, // Track which data point was last clicked,
                newCommentText: null,
                panelItems: [
                    {
                        title: 'Supporting Documents',
                        content: 'attachmentsgrid',
                        expanded: false
                    }
                ],
                saveChangesWarning: false,
                saveDocumentBtnDisabled: true,
                selectedAttachment: { url: '', id: '', title: '' },
                selectedConditionValues: [],
                selectedDocumentValue:'',
                selectedField: 'selected',
                selectedJunkedAttachment: {},
                selectedId: null,
                selectedRemovedCondition: {},
                showAttachment: false,
                showChatResponse: false,
                showConfirmCloseDialog: false,
                showConfirmDialog: false,
                showErrorDialog: false,
                showFileSplitDialog:false,
                sortable: { allowUnsort: false, mode: 'single' },
                successNotification: false,
                uneditedDocument: {},
                selectedFileBytes:'',
                isRegeneratingAI: false,
                loadingMessages: ['Loading...', 'Reviewing...', 'Verifying...', 'Determining...', 'Generating...'],
                currentLoadingIndex: 0,
                loadingInterval: null,
                currentAttachmentRequestId: null,
                currentDocumentRequestId: null,
                currentAISummaryRequestId: null,
            }
        },
        computed: {
            isDevEnvironment() {
                const hostname = window.location.hostname;
                return hostname === 'dev-swift.goldstarfinancial.com' || hostname === 'localhost';
            },
            documentList() {    
                return this.documents.map(doc => {
                return {
                    id: doc.id,
                    title: doc.title
                }
                }).sort((a, b) => a.title.localeCompare(b.title));          
            },
            formattedAISummary() {
                if (!this.editedDocument.aidescription) return '';

                let formatted = this.editedDocument.aidescription;

                // First, extract and format data points (**Label**: Value)
                formatted = this.formatDataPoints(formatted);

                // Then apply other markdown formatting
                formatted = formatted
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold (for labels not already processed)
                .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
                .replace(/^- (.*$)/gim, '$1<br>') // List items
                .replace(/\n/g, '</p><p>') // Paragraphs
                .replace(/\n/g, '<br>'); // Line breaks

                // Wrap in paragraph tags if not already wrapped
                if (!formatted.startsWith('<')) {
                formatted = '<p>' + formatted + '</p>';
                }

                return formatted;
            },
            isValidBucketForSummary() {
                if (!this.selectedAttachment.id || this.selectedAttachment.id === 'All') {
                    return false;
                }

                // Find the selected attachment in the document attachments
                const currentAttachment = this.documentAttachments.find(att => att.id === this.selectedAttachment.id);
                if (!currentAttachment || !currentAttachment.assignedTo || !currentAttachment.assignedTo.entityName) {
                    return false;
                }

                // List of valid bucket names (this should match what's in your C# _systemPrompts)
                const validBuckets = [
                'PRP - HOI/Flood Insurance',
                'PRP -Title Commitment / 12 Month\'s Chain of Title/Quit Claim Deed',
                'PRP - Purchase Agreement with All Addendums',
                'I - Tax Returns w/ Schedules - 2 Years',
                'I - Business Tax Returns',
                'PRP - Appraisal - Initial Report and Invoice',
                'I - Divorce Decree',
                'A - Bank Statements',
                'Credit Report',
                'Driver\'s License',
                'I - W2\'s (Borrower)',
                'I - W2\'s (CoBorrower)',
                ];

                return validBuckets.includes(currentAttachment.assignedTo.entityName);
            },
            chatInputPlaceholder() {
                if (this.isChatLoading) {
                    return this.loadingMessages[this.currentLoadingIndex];
                }
                return 'Ask about this document...';
            },
            isAppraisalBucket() {
                if (!this.selectedAttachment.id || this.selectedAttachment.id === 'All') {
                    return false;
                }

                const currentAttachment = this.documentAttachments.find(att => att.id === this.selectedAttachment.id);
                if (!currentAttachment || !currentAttachment.assignedTo || !currentAttachment.assignedTo.entityName) {
                    return false;
                }

                return currentAttachment.assignedTo.entityName === 'PRP - Appraisal - Initial Report and Invoice';
            },
            shouldShowAppraisalDistanceNotice() {
                if (!this.isAppraisalBucket || !this.editedDocument.aidescription) {
                    return false;
                }

                // Extract the "Farthest Comparable Proximity to Subject" value from AI summary
                const regex = /\*\*Farthest Comparable Proximity to Subject\*\*:\s*([^\n]+)/i;
                const match = this.editedDocument.aidescription.match(regex);

                if (!match || !match[1]) {
                    return false;
                }

                const proximityValue = match[1].trim();

                // Skip if value is "Data Not Found in Document"
                if (proximityValue.includes('Data Not Found')) {
                    return false;
                }

                // Parse distance - handle formats: "1.5 miles", "2 mi", "1.5", etc.
                const distanceMatch = proximityValue.match(/(\d+\.?\d*)\s*(mile|mi)?/i);

                if (!distanceMatch) {
                    return false;
                }

                const distance = parseFloat(distanceMatch[1]);

                // Show notice if distance is greater than 1 mile
                return distance > 1;
            },
            shouldShowAppraisalConditionNotice() {
              if (!this.isAppraisalBucket || !this.editedDocument.aidescription) {
                return false;
              }

              // Extract the "Condition" value from AI summary
              const regex = /\*\*Condition\*\*:\s*([^\n]+)/i;
              const match = this.editedDocument.aidescription.match(regex);

              if (!match || !match[1]) {
                return false;
              }

              const conditionValue = match[1].trim();

              // Skip if value is "Data Not Found in Document"
              if (conditionValue.includes('Data Not Found')) {
                return false;
              }

              // Parse condition rating - handle formats: "C5", "C6", "5", "6", etc.
              const conditionMatch = conditionValue.match(/C?(\d+)/i);

              if (!conditionMatch) {
                return false;
              }

              const conditionNumber = parseInt(conditionMatch[1], 10);

              // Show notice if condition is C5 or above
              return conditionNumber >= 5;
            },
            shouldShowAppraisalAdjustmentNotice() {
              if (!this.isAppraisalBucket || !this.editedDocument.aidescription) {
                return false;
              }

              let showNotice = false;

              // Extract Net Adjustment value
              const netRegex = /\*\*(?:Net Adjustment|Net Adj)\*\*:\s*([^\n]+)/i;
              const netMatch = this.editedDocument.aidescription.match(netRegex);

              if (netMatch && netMatch[1]) {
                const netValue = netMatch[1].trim();
                if (!netValue.includes('Data Not Found')) {
                  // Parse percentage - handle formats: "12%", "12.5%", etc.
                  const netPercentMatch = netValue.match(/(\d+\.?\d*)\s*%/);
                  if (netPercentMatch) {
                    const netPercent = parseFloat(netPercentMatch[1]);
                    if (netPercent > 10) {
                      showNotice = true;
                    }
                  }
                }
              }

              // Extract Gross Adjustment value
              const grossRegex = /\*\*(?:Gross Adjustment|Gross Adj)\*\*:\s*([^\n]+)/i;
              const grossMatch = this.editedDocument.aidescription.match(grossRegex);

              if (grossMatch && grossMatch[1]) {
                const grossValue = grossMatch[1].trim();
                if (!grossValue.includes('Data Not Found')) {
                  // Parse percentage - handle formats: "16%", "16.5%", etc.
                  const grossPercentMatch = grossValue.match(/(\d+\.?\d*)\s*%/);
                  if (grossPercentMatch) {
                    const grossPercent = parseFloat(grossPercentMatch[1]);
                    if (grossPercent > 15) {
                      showNotice = true;
                    }
                  }
                }
              }

              return showNotice;
            },
          },

        mounted() {
        },
        watch: {
            showDialog(val) {
                if (val) {
                    this.processSelectedDocument();
                    this.documentCommentsLoading = true;
                    this.getDocumentComments();
                } else {
                    // Invalidate any in-flight requests when dialog closes
                    this.currentAttachmentRequestId = null;
                    this.currentDocumentRequestId = null;
                    this.currentAISummaryRequestId = null;

                    this.documentUpdated = false;
                    this.conditionsAdded = false;
                    this.saveDocumentBtnDisabled = true;
                    this.showAttachment = false;

                    this.condSearchWord = '';
                    this.selectedAttachment = { url: '', id: '', title: '' };
                    this.selectedConditionValues = [];
                    this.editedDocument = {};
                    this.uneditedDocument = {};
                    this.documentChatInput = '';

                    this.isLoading = false;
                    this.isAttachmentLoading = false;
                    this.saveChangesWarning = false;
                    this.selectedId = null;
                    this.selectedDocumentValue = '';
                    this.isAISummaryLoading = false;
                    this.aiSummaryFetched = false;
                    this.validatedDataPoints = new Set();
                    this.dataPointSearchState = {}; // Reset search states
                    this.lastClickedDataPointId = null; // Reset last clicked tracking
                    this.isValidatingDataPoints = false;

                    this.documentComments = [];
                    this.documentCommentsLoading = false;
                    this.newCommentText = null;

                    this.stopLoadingAnimation();
                }
            },
            document(val) {
                this.isLoading = false;
                this.processSelectedDocument();
            },
            selectedConditionValues(val) {
                if (val.length > 0) {
                    this.conditionsAdded = true;
                } else {
                    this.conditionsAdded = false;
                }

                this.checkForDocumentChanges();
            },
            'editedDocument.aidescription': {
                handler(newValue) {
                    if (newValue) {
                        // Trigger validation - retry logic will handle WebViewer not being ready
                        setTimeout(() => this.validateDataPoints(), 100);
                    } else {
                        this.validatedDataPoints.clear();
                    }
                },
                deep: true
            },
            selectedAttachment: {
                handler(newAttachment, oldAttachment) {
                    if (newAttachment && newAttachment.id && newAttachment.id !== oldAttachment?.id) {
                        // Reset validation when attachment changes  
                        this.validatedDataPoints.clear();
                        // Don't trigger validation here - let the onWebViewerLoaded handle it
                    }
                },
                deep: true
            },         
        },
        methods: {
            assignDocumentComments(commentData) {
                commentData.forEach(comment => {
                    comment.addedDate = new Date(comment.addedDate);
                });

                this.documentComments = commentData;
                this.scrollComments();
            },
            attachmentSortChangeHandler(evt) {
                this.attachmentSort = evt.sort;

                this.documentAttachments = orderBy(this.documentAttachments, this.attachmentSort);
            },
            backToSummary() {
              this.showChatResponse = false;
              this.documentChatResponse = '';
              this.chatError = null;
            },
            base64ToBlob(base64, type='application/octet-stream') {
                const binStr = atob(base64);
                const len = binStr.length;
                const arr = new Uint8Array(len);

                for (let i = 0; i< len; i++) {
                    arr[i] = binStr.charCodeAt(i);
                }
                
                return new Blob([arr], {type: type});
            },
            cancelJunkAttachment() {
                this.selectedJunkedAttachment = {};
                this.toggleConfirmDialog();
            },
            cancelRemoveCondition() {
                this.selectedRemovedCondition = {};
                this.toggleConfirmDialog();
            },
            checkForDocumentChanges() {
                if (!isEqual(this.editedDocument, this.uneditedDocument)) {
                    this.documentUpdated = true;
                } else {
                    this.documentUpdated = false;
                }

                if (this.documentUpdated || this.conditionsAdded) {
                    return this.saveDocumentBtnDisabled = false;
                }

                this.saveDocumentBtnDisabled = true;
                this.saveChangesWarning = false;
            },
            clearChatHistory() {
              this.documentChatMessages = [];
              this.chatError = null;
            },
            startLoadingAnimation() {
              this.currentLoadingIndex = 0;
              this.loadingInterval = setInterval(() => {
                this.currentLoadingIndex = (this.currentLoadingIndex + 1) % this.loadingMessages.length;
              }, 5000);
            },
            stopLoadingAnimation() {
              if (this.loadingInterval) {
                clearInterval(this.loadingInterval);
                this.loadingInterval = null;
              }
              this.currentLoadingIndex = 0;
            },
            closeDialog() {
                if (!this.saveDocumentBtnDisabled) return this.toggleConfirmCloseDialog();

                this.$emit('dialogclosed');
            },
            closeWarningDialog() {
                this.selectedRemovedCondition = {};
                this.selectedJunkedAttachment = {};
                this.toggleConfirmDialog();
            },
            confirmCloseDialog() {
                this.showConfirmCloseDialog = !this.showConfirmCloseDialog;

                this.$emit('dialogclosed');
            },
            confirmFileSplitDialog() {
                this.uploadFile({
                    loanId: this.loanId,
                    fileName: this.fileTitle,
                    contentType: 'application/pdf',
                    file: this.selectedFileBytes,
                    documentId: this.selectedDocumentValue.id
                })
                
                this.showFileSplitDialog = false;
            },
            async createDocumentComment(request) {
                if (this.document) {
                    this.isLoading = true;

                    let tokenResponse = await auth.acquireTokenResponse();
                    this.idTokenClaims = tokenResponse.idTokenClaims;

                    apiService.CreateDocumentComment(this.loanId, this.document.id, request, tokenResponse.accessToken)
                        .then(res => {
                            if (res.data?.data) {
                                this.newCommentText = null;
                                this.assignDocumentComments(res.data.data);
                            }

                            this.isLoading = false;

                            this.$emit('shownotification', 'Comment added successfully');
                        });
                }
            },
            documentTitleChanged() {},

            async fetchAISummary(attachmentId) {
              if (!attachmentId || attachmentId === 'All') {
                this.editedDocument.aidescription = '';
                this.aiSummaryFetched = false;
                return;
              }

              // Generate unique request ID to track this specific request
              const requestId = Date.now() + Math.random();
              this.currentAISummaryRequestId = requestId;

              this.isAISummaryLoading = true;
              this.editedDocument.aidescription = '';

              try {
                const tokenResponse = await auth.acquireTokenResponse();
                const response = await apiService.GetAttachmentAISummary(attachmentId, tokenResponse.accessToken);

                // Check if this request is still current before applying results
                if (this.currentAISummaryRequestId !== requestId) {
                  return; // Abort - a newer request has started
                }

                this.editedDocument.aidescription = response.data.summary || '';
                this.aiSummaryFetched = !!response.data.summary;
              } catch (error) {
                // Check if this request is still current before handling error
                if (this.currentAISummaryRequestId !== requestId) {
                  return; // Abort - a newer request has started
                }

                console.error("Failed to fetch AI summary for attachment " + attachmentId + ":", error);
                this.editedDocument.aidescription = 'Error loading AI summary.';
              } finally {
                // Only update loading state if this request is still current
                if (this.currentAISummaryRequestId === requestId) {
                  this.isAISummaryLoading = false;
                  this.aiSummaryFetched = true;
                }
              }
            },
            fileSplitStarted(evt) {
              this.showFileSplitDialog = true;
              this.fileTitle = evt.title;
              this.selectedDocumentValue = this.documentList.find(x=> x.id === evt.bucketId);
              this.selectedFileBytes = evt.file;
            },
            formatChatMessage(content) {
              if (!content) return '';

              // Convert markdown-style formatting to HTML
              return content
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // Bold
                .replace(/\*(.*?)\*/g, '<em>$1</em>') // Italic
                .replace(/\n/g, '<br>') // Line breaks
                .trim();
            },
            formatTimestamp(timestamp) {
              if (!timestamp) return '';

              const now = new Date();
              const diff = now - timestamp;
              const minutes = Math.floor(diff / 60000);

              if (minutes < 1) return 'Just now';
              if (minutes < 60) return `${minutes}m ago`;

              const hours = Math.floor(minutes / 60);
              if (hours < 24) return `${hours}h ago`;

              return timestamp.toLocaleDateString();
            },
            getAddedConditions() {
                let addedConditions = this.selectedConditionValues.map(cond => {
                    return {
                        conditionId: cond.id,
                        documents: [{
                            entityId: this.document.id,
                            entityType: 'Document'
                        }]
                    }
                });

                return addedConditions;
            },
            async getDocumentComments() {
                if (this.document) {
                    let tokenResponse = await auth.acquireTokenResponse();
                    this.idTokenClaims = tokenResponse.idTokenClaims;

                    apiService.GetDocumentComments(this.loanId, this.document.id, tokenResponse.accessToken) 
                        .then(res => {
                            if (res.data?.data) {
                                this.assignDocumentComments(res.data.data);
                            }

                            this.documentCommentsLoading = false;
                        }).catch(error => {
                            this.documentCommentsLoading = false;
                            this.errorTitle = 'Failed to Fetch Document Comments';

                            if (error?.response?.data?.data) {
                                this.errorMsg = "Error: \n\n" + error.response.data.data;
                            } else {
                                this.errorMsg = '';
                            }

                            this.onRequestError(this.errorTitle, this.errorMsg);
                        });
                }
            },
            async RemoveAttachments(id) {
              let tokenResponse = await auth.acquireTokenResponse();
              this.idTokenClaims = tokenResponse.idTokenClaims;

              let removedAttachment = {
                entityId: id,
                entityType: 'Attachment'
              }
          
              apiService.MoveAttachmentToJunkFolder(this.loanId, this.junkFolderId, removedAttachment, tokenResponse.accessToken)
                .then(res => {
                    this.$emit('documentupdated', this.document.id);          
                }).catch(error => {
                    this.errorTitle = 'Failed to Remove Attachment';

                    if (error?.response?.data?.data) {
                        this.errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        this.errorMsg = '';
                    }

                    this.onRequestError(this.errorTitle, this.errorMsg);
                });
            },
            async junkAttachment() {
                this.$emit('shownotification', 'Removing attachment...', true);
                this.toggleConfirmDialog();

                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let removedAttachment = {
                    entityId: this.selectedJunkedAttachment.id,
                    entityType: 'Attachment'
                }

                apiService.MoveAttachmentToJunkFolder(this.loanId, this.junkFolderId, removedAttachment, tokenResponse.accessToken)
                    .then(res => {
                        this.$emit('documentupdated', this.document.id);

                        this.$emit('closenotification', 'progressNotification');
                        this.$emit('shownotification', 'Attachment removed successfully');
                    }).catch(error => {
                        this.$emit('closenotification', 'progressNotification');
                        this.errorTitle = 'Failed to Remove Attachment';

                        if(error?.response?.data?.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            onCloseNotification(flag) {
                this[flag] = false;
            },
            onConditionFilterChange(evt) {
                this.filteredConditionList = filterBy(this.conditionList, evt.filter);
            },
            onConditionSelected(evt) {
                this.checkForDocumentChanges();
            },
            onCreateComment() {
                if (!this.newCommentText) return;

                let request = {
                    isExternal: false,
                    comments: this.newCommentText
                }

                this.createDocumentComment(request);
            },
            onCurrentVersionChanged(attachment) {
                let request = {
                    id: this.document.id,
                    entityId: this.document.id,
                    files: [
                        {
                            entityId: attachment.id,
                            isActive: attachment.isActive
                        }
                    ]
                }

                this.updateAttachmentCurrentVersion(request);
            },
            async onDocumentChatInput(evt) {
              // Handle Enter key press
              if (evt.key === 'Enter' && !evt.shiftKey) {
                evt.preventDefault();
                await this.sendChatMessage();
              }
            },
            async onOpenAttachment(evt) {
              if (this.selectedAttachment.id === evt.dataItem.id) return;

                // Generate unique request ID to track this specific request
                const requestId = Date.now() + Math.random();
                this.currentAttachmentRequestId = requestId;

                this.aiSummaryFetched = false;

                if (this.editedDocument) this.editedDocument.aidescription = "";

                this.backToSummary();

                this.selectedId = evt.dataItem.id;

                this.documentAttachments.forEach(att => {
                  if (att.id === this.selectedId) {
                    att.selected = true;
                  } else {
                    att.selected = false;
                  }
                });

                this.showAttachment = false;

                this.selectedAttachment.url = '';
                this.selectedAttachment.id = evt.dataItem.id;
                this.selectedAttachment.title = evt.dataItem.title;

                this.isAttachmentLoading = true;
                await this.fetchAISummary(evt.dataItem.id);

                // Check if this request is still current after async AI summary fetch
                if (this.currentAttachmentRequestId !== requestId) {
                    return; // Abort - a newer request has started
                }

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let attachmentsFile = {
                    loanId: this.loanId,
                    attachments: [evt.dataItem.id]
                };

                apiService.GetAttachmentsFileBytes(attachmentsFile, tokenResponse.accessToken, evt.dataItem.isActive ? "false" : "true")
                    .then(res => {
                        // Check if this request is still current before applying results
                        if (this.currentAttachmentRequestId !== requestId) {
                            return; // Abort - a newer request has started
                        }

                        const blob = this.base64ToBlob(res.data.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);

                        this.isAttachmentLoading = false;
                        this.selectedAttachment.url = url;
                        this.showAttachment = true;
                        this.uneditedDocument.aidescription = this.editedDocument.aidescription;
                        this.checkForDocumentChanges();
                    }).catch(error => {
                        // Check if this request is still current before handling error
                        if (this.currentAttachmentRequestId !== requestId) {
                            return; // Abort - a newer request has started
                        }

                        this.isAttachmentLoading = false;
                        this.errorTitle = 'Failed to Open Attachment';

                        if(error?.response?.data?.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                        this.uneditedDocument.aidescription = this.editedDocument.aidescription;
                        this.checkForDocumentChanges();
                    });
            },
            onJunkAttachment(attachment) {
                if (!this.saveDocumentBtnDisabled) {
                    this.saveChangesWarning = true;
                }

                this.selectedJunkedAttachment = attachment;

                this.confirmJunkAttachment = true;
                this.toggleConfirmDialog(); 
            },
            onRemoveCondition(condition) {
                if (!this.saveDocumentBtnDisabled) {
                    this.saveChangesWarning = true;
                }

                this.selectedRemovedCondition = condition;

                this.confirmRemoveCondition = true;
                this.toggleConfirmDialog();
            },
            onRequestError(errorTitle, errorMsg) {
                this.isLoading = false;
                this.isAttachmentLoading = false;
                this.errorAlert = true;

                this.errorTitle = errorTitle;

                if (errorMsg) {
                    this.errorMsg = errorMsg;
                } else {
                    this.errorMsg = 'Please try again';
                }

                if (!this.showErrorDialog) {
                    this.toggleErrorDialog();
                }
            },
            onSearchConditions(evt) {
                this.condSearchWord = evt.value;

                if (this.condSearchWord) {
                    this.filteredCurrentConditionList = this.editedDocument.conditions.filter(cond => cond.entityName.toLowerCase().includes(this.condSearchWord.toLowerCase()));
                } else {
                    this.filteredCurrentConditionList = this.editedDocument.conditions.map(cond => ({...cond}));
                }
            },
            async onViewAllFiles() {
                if (this.documentAttachments?.length === 0 || this.selectedAttachment.id === 'All') return;

                // Generate unique request ID to track this specific request
                const requestId = Date.now() + Math.random();
                this.currentAttachmentRequestId = requestId;

                this.aiSummaryFetched = false;

                this.selectedId = 'All';

                this.documentAttachments.forEach(att => {
                  if (att.isActive) {
                    att.selected = true;
                  } else {
                    att.selected = false;
                  }
                });

                let attachments = this.documentAttachments.map(att => att.id);

                this.showAttachment = false;

                this.selectedAttachment.url = '';
                this.selectedAttachment.id = 'All';
                this.selectedAttachment.title = this.document.title;
                this.editedDocument.aidescription = '';
                if (this.uneditedDocument) this.uneditedDocument.aidescription = '';
                this.isAttachmentLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let attachmentsFile = {
                    loanId: this.loanId,
                    attachments: attachments
                };

                apiService.GetMergedAttachmentsFileBytes(attachmentsFile, tokenResponse.accessToken)
                    .then(res => {
                        // Check if this request is still current before applying results
                        if (this.currentAttachmentRequestId !== requestId) {
                            return; // Abort - a newer request has started
                        }

                        const blob = this.base64ToBlob(res.data.data, 'application/pdf');
                        const url = URL.createObjectURL(blob);

                        this.isAttachmentLoading = false;
                        this.selectedAttachment.url = url;
                        this.showAttachment = true;
                    }).catch(error => {
                        // Check if this request is still current before handling error
                        if (this.currentAttachmentRequestId !== requestId) {
                            return; // Abort - a newer request has started
                        }

                        this.isAttachmentLoading = false;
                        this.errorTitle = 'Failed to Open Attachment';

                        if(error?.response?.data?.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            processSelectedDocument() {
                // Generate unique request ID to track this document processing
                const requestId = Date.now() + Math.random();
                this.currentDocumentRequestId = requestId;

                // Handle case where document is null (loading state)
                if (!this.document) {
                    this.documentAttachments = [];
                    return;
                }

                if (this.document.title.includes('Junk')) {
                    this.attachmentColumns[3].hidden = true;
                } else {
                    this.attachmentColumns[3].hidden = false;
                }

                this.documentAttachments = this.document.allAttachments?.map(att => ({...att})) || [];

                if (this.documentAttachments.length > 0) {
                    this.documentAttachments.forEach(att => att.selected = false);
                    this.attachmentSort = [{ dir: 'desc', field: 'createdDate' }];
                    this.documentAttachments = orderBy(this.documentAttachments, this.attachmentSort);

                    if (this.selectedId === 'All') {
                      this.selectedAttachment.id = null;
                      return this.onViewAllFiles();
                    }

                    if (this.documentAttachments.some(att => this.selectedId === att.id)) {
                      return this.documentAttachments.find(att => this.selectedId === att.id).selected = true;
                    }

                    this.onOpenAttachment({ dataItem: { id: this.documentAttachments[0].id, title: this.documentAttachments[0].title, isActive: this.documentAttachments[0].isActive }});
                } else {
                  this.selectedAttachment = { url: '', id: '', title: '' };
                }

                this.editedDocument = {...this.document};

                if(this.editedDocument.description === null) {
                    this.editedDocument.description = '';
                }

                if (this.editedDocument.conditions && this.editedDocument.conditions.length > 0) {
                    this.conditionList = this.loanConditions.filter(cond => {
                        return !this.editedDocument.conditions.some(cur => cur.entityName === cond.title)
                    });
                } else {
                    this.editedDocument.conditions = [];
                    this.conditionList = this.loanConditions.map(cond => ({...cond}));
                }

                this.uneditedDocument = {...this.editedDocument};

                this.filteredConditionList = this.conditionList.map(cond => ({...cond}));

                this.filteredCurrentConditionList = this.editedDocument.conditions.map(cond => ({...cond}));

                this.checkForDocumentChanges();

                this.$nextTick(() => {
                    let panelTextEl = document.querySelector('.k-panelbar-item-text');

                    if (panelTextEl) {
                        panelTextEl.innerHTML += `<span class="attachments-badge">${this.documentAttachments.length}</span>`;
                    }
                })
            },
            async generateAISummary() {
              if (!this.selectedAttachment.id || this.selectedAttachment.id === 'All') {
                this.$emit('shownotification', 'Please select a specific attachment to generate summary', false);
                return;
              }

              this.isRegeneratingAI = true;

              try {
                const tokenResponse = await auth.acquireTokenResponse();

                const response = await apiService.RegenerateDocumentSummary(
                  this.loanId,
                  this.selectedAttachment.id,
                  tokenResponse.accessToken
                );

                if (response.data.success) {
                  this.editedDocument.aidescription = response.data.summary;
                  this.uneditedDocument.aidescription = response.data.summary;
                  this.aiSummaryFetched = true; // Mark as fetched so it shows the summary area
                  this.checkForDocumentChanges();
                  this.$emit('shownotification', 'AI Summary generated successfully');
                } else {
                  throw new Error('Failed to generate summary');
                }
              } catch (error) {
                console.error('Failed to generate AI summary:', error);
                this.$emit('shownotification', 'Failed to generate AI summary. Please try again.', false);
              } finally {
                this.isRegeneratingAI = false;
              }
            },
            async regenerateAISummary() {
              if (!this.selectedAttachment.id || this.selectedAttachment.id === 'All') {
                this.$emit('shownotification', 'Please select a specific attachment to regenerate summary', false);
                return;
              }

              this.isRegeneratingAI = true;

              try {
                const tokenResponse = await auth.acquireTokenResponse();

                // Call the new API endpoint
                const response = await apiService.RegenerateDocumentSummary(
                  this.loanId,
                  this.selectedAttachment.id,
                  tokenResponse.accessToken
                );

                if (response.data.success) {
                  this.editedDocument.aidescription = response.data.summary;
                  this.uneditedDocument.aidescription = response.data.summary;
                  this.checkForDocumentChanges();
                  this.$emit('shownotification', 'AI Summary regenerated successfully');
                } else {
                  throw new Error('Failed to regenerate summary');
                }
              } catch (error) {
                console.error('Failed to regenerate AI summary:', error);
                this.$emit('shownotification', 'Failed to regenerate AI summary. Please try again.', false);
              } finally {
                this.isRegeneratingAI = false;
              }
            },
            async removeCondition() {
                this.$emit('shownotification', 'Removing condition...', true);
                this.toggleConfirmDialog();

                this.isLoading = true;

                let tokenResponse = await auth.acquireTokenResponse();
                this.idTokenClaims = tokenResponse.idTokenClaims;

                let removedDocuments = [{
                    entityId: this.document.id,
                    entityType: 'Document'
                }]

                apiService.RemoveDocumentCondition(this.loanId, this.selectedRemovedCondition.entityId, removedDocuments, tokenResponse.accessToken)
                    .then(res => {
                        this.$emit('documentupdated', removedDocuments[0].entityId);

                        this.$emit('closenotification', 'progressNotification');
                        this.$emit('shownotification', 'Condition removed successfully');
                    }).catch(error => {
                        this.$emit('closenotification', 'progressNotification');
                        this.errorTitle = 'Failed to Remove Condition';

                        if(error?.response?.data?.data) {
                          this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });
            },
            async saveDocument() {
                if (this.saveDocumentBtnDisabled) return this.closeDialog();

                this.saveChangesWarning = false;

                if (this.documentUpdated) {
                    return await this.updateDocument();
                }

                if (this.conditionsAdded) {
                    let addedConditions = this.getAddedConditions();
                    this.$emit('updatedocumentconditions', cloneDeep(addedConditions), this.document.id);
                }
            },
            scrollComments() {
                if (this.documentComments?.length === 0) return;

                this.$nextTick(() => {
                    let commentsContainer = document.querySelector('.document-comments');

                    if (commentsContainer) {
                        commentsContainer.scrollTop = commentsContainer.scrollHeight;
                    }
                });
            },
            async sendChatMessage() {
              if (!this.documentChatInput.trim() || this.isChatLoading) return;

              if (!this.selectedAttachment.id || this.selectedAttachment.id === 'All') {
                this.chatError = 'Please select a specific document to chat with.';
                return;
              }

              const userMessage = this.documentChatInput.trim();
              this.documentChatInput = '';
              this.isChatLoading = true;
              this.chatError = null;
              this.showChatResponse = true;
              this.documentChatResponse = '';

              this.startLoadingAnimation();

              try {
                const tokenResponse = await auth.acquireTokenResponse();

                // Call your API endpoint for document chat
                const response = await apiService.SendDocumentChatMessage(
                  this.loanId,
                  this.selectedAttachment.id,
                  userMessage,
                  tokenResponse.accessToken
                );

                if (!response.ok) {
                  throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Handle streaming response
                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                while (true) {
                  const { done, value } = await reader.read();
                  if (done) break;

                  const chunk = decoder.decode(value, { stream: true });
                  this.documentChatResponse += chunk;
                  
                  // Force Vue to update the DOM immediately during streaming
                  this.$nextTick();
                }

              } catch (error) {
                console.error('Chat error:', error);
                this.chatError = 'Failed to send message. Please try again.';
                this.showChatResponse = false;
              } finally {
                this.isChatLoading = false;
                this.stopLoadingAnimation();
              }
            },
            showNotification(notificationMsg) {
                this.notificationMsg = notificationMsg;
                this.successNotification = true;

                setTimeout(() => {
                    this.successNotification = false;
                    this.notificationMsg = '';
                }, 4000);
            },
            toggleConfirmCloseDialog() {
                this.showConfirmCloseDialog = !this.showConfirmCloseDialog;
            },
            toggleConfirmDialog() {
                this.showConfirmDialog = !this.showConfirmDialog;

                if (!this.showConfirmDialog) {
                    this.confirmJunkAttachment = false;
                    this.confirmRemoveCondition = false;
                }
            },
            toggleErrorDialog() {
                this.showErrorDialog = !this.showErrorDialog;

                if(!this.showErrorDialog) {
                    this.errorAlert = false;
                    this.errorMsg = '';
                }
            },
            toggleFileSplitDialog() {
                this.showFileSplitDialog = !this.showFileSplitDialog;
            },
            async updateAttachmentCurrentVersion(request) {
                this.$emit('shownotification', 'Updating attachment...', true);

                let tokenResponse = await auth.acquireTokenResponse();

                return apiService.UpdateAttachmentCurrentVersion(this.loanId, request, tokenResponse.accessToken)
                    .then(res => {
                        this.$emit('documentupdated', this.document.id);

                        this.$emit('closenotification', 'progressNotification');
                        this.$emit('shownotification', 'Attachment updated successfully');
                    }).catch(error => {
                        this.$emit('closenotification', 'progressNotification');
                        this.errorTitle = 'Failed to Update Attachment';

                        if (error?.response?.data?.data) {
                            this.errorMsg = "Error: \n\n" + error.response.data.data;
                        } else {
                            this.errorMsg = '';
                        }

                        this.onRequestError(this.errorTitle, this.errorMsg);
                    });

            },
            async updateDocument() {
                let obj = {
                    loanId: this.loanId,
                    description: this.editedDocument.description,
                    documentId: this.editedDocument.id
                };

                if (!this.conditionsAdded) {
                    this.$emit('updatedocument', cloneDeep(obj), this.conditionsAdded);
                } else {
                    let addedConditions = this.getAddedConditions();
                    this.$emit('updatedocument', cloneDeep(obj), this.conditionsAdded, cloneDeep(addedConditions));
                }
            },
          async uploadFile(attachment) {
              this.$emit('shownotification', 'Splitting attachment...', true);
              let self = this;
              let tokenResponse = await auth.acquireTokenResponse();
              this.idTokenClaims = tokenResponse.idTokenClaims;

            apiService.UploadAttachmentAndPutInBucket(attachment, tokenResponse.accessToken)
                .then(res => {
                    self.showNotification("File uploaded Successful");
                    self.$emit('closenotification', 'progressNotification');
                    self.$emit('documentupdated', self.document.id);
                }).catch(error => {
                    self.$emit('closenotification', 'progressNotification');

                    this.errorTitle = 'Failed to Edit Attachment';

                    if (error?.response?.data?.data) {
                        this.errorMsg = "Error: \n\n" + error.response.data.data;
                    } else {
                        this.errorMsg = '';
                    }

                    this.onRequestError(this.errorTitle, this.errorMsg);
                });   
            },
            formatDataPoints(text) {
                if (!text) return text;

                // Find all occurrences of **Label**: Value pattern
                return text.replace(/\*\*([^*]+)\*\*:\s*([^\n]+)/g, (match, label, value) => {
                    const trimmedValue = value.trim();
                    
                    // Only create searchable data points for meaningful values (not empty, not just punctuation)
                    if (trimmedValue.length > 0 && !/^[^\w]*$/.test(trimmedValue)) {
                        const dataPointId = `dp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                        
                        // Check if this data point or any of its parts are validated
                        const isCompleteValidated = this.validatedDataPoints.has(trimmedValue);
                        const parts = this.breakDownDataPoint(trimmedValue);
                        const hasValidatedParts = parts.some(part => this.validatedDataPoints.has(part));
                        
                        if (isCompleteValidated || hasValidatedParts) {
                            // If complete value OR any part is found, make the whole thing searchable
                            return `**${label}**: <span class="ai-data-point-wrapper" data-search-term="${this.escapeHtml(trimmedValue)}" data-dp-id="${dataPointId}">${this.escapeHtml(trimmedValue)}<span class="magnifying-glass"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="12" height="12" fill="currentColor"><path d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"/></svg></span></span>`;
                        } else {
                            return `**${label}**: ${this.escapeHtml(trimmedValue)}`;
                        }
                    }
                    
                    return match; // Return original if not a valid data point
                });
            },
            escapeRegex(string) {
                return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            },
            escapeHtml(text) {
                const div = document.createElement('div');
                div.textContent = text;
                return div.innerHTML;
            },
            async validateDataPoints(retryCount = 0) {
                if (this.isValidatingDataPoints || !this.editedDocument.aidescription) return;
                
                this.isValidatingDataPoints = true;
                this.validatedDataPoints.clear();
                
                try {
                    // Extract all data point values from the AI description
                    const dataPointValues = this.extractDataPointValues(this.editedDocument.aidescription);
                    
                    if (dataPointValues.length === 0) {
                        return;
                    }
                    
                    // Get reference to WebViewer with retry logic
                    const webViewer = this.$refs.webViewer;
                    if (!webViewer || !webViewer.pdfInstance) {
                        
                        // Retry up to 5 times with increasing delays
                        if (retryCount < 5) {
                            this.isValidatingDataPoints = false;
                            const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Exponential backoff, max 5s
                            setTimeout(() => this.validateDataPoints(retryCount + 1), delay);
                        } else {
                            console.warn('WebViewer not ready after 5 attempts, skipping validation');
                        }
                        return;
                    }
                    
                    // Double-check that the PDF instance has search capability
                    if (typeof webViewer.pdfInstance.search !== 'function') {
                        if (retryCount < 3) {
                            this.isValidatingDataPoints = false;
                            setTimeout(() => this.validateDataPoints(retryCount + 1), 1000);
                        }
                        return;
                    }
                    
                    
                    // Test each data point value against the PDF
                    for (const value of dataPointValues) {
                        try {
                            const results = await webViewer.pdfInstance.search(value);
                            if (results && results.size > 0) {
                                this.validatedDataPoints.add(value);
                            }
                        } catch (error) {
                            console.error('Error validating data point:', value, error);
                        }
                    }
                    
                    
                    // Force re-render of the AI summary to show validated data points
                    this.$forceUpdate();
                    
                } finally {
                    this.isValidatingDataPoints = false;
                }
            },
            extractDataPointValues(text) {
                const values = [];
                const regex = /\*\*([^*]+)\*\*:\s*([^\n]+)/g;
                let match;
                
                while ((match = regex.exec(text)) !== null) {
                    const value = match[2].trim();
                    if (value.length > 0 && !/^[^\w]*$/.test(value)) {
                        // Add the complete value
                        values.push(value);
                        
                        // Break down complex values into searchable parts
                        const parts = this.breakDownDataPoint(value);
                        values.push(...parts);
                    }
                }
                
                // Remove duplicates and return
                return [...new Set(values)];
            },
            breakDownDataPoint(value) {
                // Cache to avoid excessive logging for same values
                if (!this._breakDownCache) this._breakDownCache = new Map();
                if (this._breakDownCache.has(value)) {
                    return this._breakDownCache.get(value);
                }
                
                const parts = [];
                
                // Handle date ranges: "March 30, 2020 to March 30, 2021"
                const dateRangePattern = /([A-Za-z]+ \d{1,2}[,.]\s?\d{4})\s+(?:to|through|thru|-|–|—)\s+([A-Za-z]+ \d{1,2}[,.]\s?\d{4})/;
                const dateRangeMatch = value.match(dateRangePattern);
                if (dateRangeMatch) {
                    const firstDate = dateRangeMatch[1].trim();
                    const secondDate = dateRangeMatch[2].trim();
                    
                    parts.push(firstDate);
                    parts.push(secondDate);
                    
                    // Also add variations with different punctuation
                    const firstDateVariations = this.getDateVariations(firstDate);
                    const secondDateVariations = this.getDateVariations(secondDate);
                    
                    parts.push(...firstDateVariations);
                    parts.push(...secondDateVariations);
                    
                }
                
                // Handle addresses: "1427 Sample Drive, Washington, DC 40000"
                const addressPattern = /^(\d+\s+[^,]+),?\s*(.+)$/;
                const addressMatch = value.match(addressPattern);
                if (addressMatch) {
                    const streetPart = addressMatch[1].trim();
                    const cityStateZip = addressMatch[2].trim();
                    
                    parts.push(streetPart); // Street number and name
                    parts.push(cityStateZip); // City, state, zip
                    
                    // Further break down city, state, zip
                    const cityStateZipParts = cityStateZip.split(',').map(part => part.trim()).filter(part => part.length > 0);
                    parts.push(...cityStateZipParts);
                    
                    // Also try variations without commas for addresses
                    const noCommaAddress = value.replace(/,/g, '');
                    if (noCommaAddress !== value) {
                        parts.push(noCommaAddress);
                    }
                    
                    // Extract just the zip code separately
                    const zipMatch = cityStateZip.match(/\b\d{5}(?:-\d{4})?\b/);
                    if (zipMatch) {
                        parts.push(zipMatch[0]);
                    }
                    
                }
                
                // Handle monetary amounts with descriptions: "$450,000 dwelling coverage"
                const moneyPattern = /(\$[\d,]+(?:\.\d{2})?)/g;
                let moneyMatch;
                while ((moneyMatch = moneyPattern.exec(value)) !== null) {
                    parts.push(moneyMatch[1]);
                }
                
                // Handle policy/contract numbers, phone numbers, etc.
                const numberPattern = /\b\d{3,}\b/g;
                let numberMatch;
                while ((numberMatch = numberPattern.exec(value)) !== null) {
                    if (numberMatch[0].length >= 4) { // Only meaningful numbers
                        parts.push(numberMatch[0]);
                    }
                }
                
                // Split by common delimiters and extract meaningful chunks
                const delimiters = /[,;|\n]/;
                const chunks = value.split(delimiters)
                    .map(chunk => chunk.trim())
                    .filter(chunk => chunk.length > 3 && !/^[^\w]*$/.test(chunk));
                parts.push(...chunks);
                
                const filteredParts = parts.filter(part => part !== value); // Don't include the original value again
                
                // Cache the result
                this._breakDownCache.set(value, filteredParts);
                
                return filteredParts;
            },
            getDateVariations(dateString) {
                const variations = [];
                
                // Try different punctuation and spacing variations
                // "March 30, 2020" -> ["March 30. 2020", "March 30 2020", "March30, 2020", etc.]
                
                if (dateString.includes(',')) {
                    // Replace comma with period
                    variations.push(dateString.replace(',', '.'));
                    // Remove comma entirely
                    variations.push(dateString.replace(',', ''));
                    // Replace comma with period and remove space
                    variations.push(dateString.replace(', ', '. '));
                }
                
                if (dateString.includes('.')) {
                    // Replace period with comma
                    variations.push(dateString.replace('.', ','));
                    // Remove period entirely
                    variations.push(dateString.replace('.', ''));
                    // Replace period with comma and ensure space
                    variations.push(dateString.replace('. ', ', '));
                }
                
                // Also try just the month and day part (in case year is separate)
                const monthDayMatch = dateString.match(/([A-Za-z]+ \d{1,2})[,.]/);
                if (monthDayMatch) {
                    variations.push(monthDayMatch[1]);
                }
                
                // Add the year separately
                const yearMatch = dateString.match(/\d{4}/);
                if (yearMatch) {
                    variations.push(yearMatch[0]);
                }
                
                return variations;
            },
            async onDataPointClick(event) {
                const target = event.target.closest('.ai-data-point-wrapper');
                if (!target) return;
                
                event.preventDefault();
                event.stopPropagation();
                
                const searchTerm = target.getAttribute('data-search-term');
                const dataPointId = target.getAttribute('data-dp-id');
                
                if (!searchTerm) return;
                
                // Get reference to WebViewer  
                const webViewer = this.$refs.webViewer;
                if (!webViewer || typeof webViewer.searchAndHighlight !== 'function') {
                    console.error('WebViewer search function not available');
                    return;
                }
                
                // If this is a different data point than the last one clicked, reset its cycling
                if (this.lastClickedDataPointId && this.lastClickedDataPointId !== dataPointId) {
                    if (this.dataPointSearchState[dataPointId]) {
                        this.dataPointSearchState[dataPointId].currentIndex = 0;
                    }
                }
                this.lastClickedDataPointId = dataPointId;
                
                // Initialize or get search state for this data point
                if (!this.dataPointSearchState[dataPointId]) {
                    const parts = this.breakDownDataPoint(searchTerm);
                    
                    const validParts = parts.filter(part => this.validatedDataPoints.has(part));
                    
                    // Sort by length descending, but prioritize street addresses over city/state
                    const sortedValidParts = validParts.sort((a, b) => {
                        // Check if this looks like an address breakdown
                        const aIsStreet = /^\d+\s+/.test(a); // Starts with number and space (street address)
                        const bIsStreet = /^\d+\s+/.test(b);
                        
                        // If one is a street address and the other isn't, prioritize the street address
                        if (aIsStreet && !bIsStreet) return -1;
                        if (!aIsStreet && bIsStreet) return 1;
                        
                        // Otherwise sort by length descending
                        return b.length - a.length;
                    });
                    
                    
                    // Create search options: full term first, then valid parts
                    const searchOptions = [];
                    if (this.validatedDataPoints.has(searchTerm)) {
                        searchOptions.push(searchTerm);
                    }
                    searchOptions.push(...sortedValidParts.filter(part => part !== searchTerm));
                    
                    this.dataPointSearchState[dataPointId] = {
                        searchOptions: searchOptions,
                        currentIndex: 0
                    };
                    
                }
                
                const searchState = this.dataPointSearchState[dataPointId];
                
                if (searchState.searchOptions.length === 0) {
                    console.warn('No valid search options for:', searchTerm);
                    return;
                }
                
                // Get current search option
                const currentSearchTerm = searchState.searchOptions[searchState.currentIndex];
                
                try {
                    const success = await webViewer.searchAndHighlight(currentSearchTerm);
                    if (success) {
                        
                        // Advance to next option for next click (cycle through)
                        searchState.currentIndex = (searchState.currentIndex + 1) % searchState.searchOptions.length;
                    } else {
                        console.warn('❌ Search failed for:', currentSearchTerm);
                        
                        // Try next option immediately if current one fails
                        searchState.currentIndex = (searchState.currentIndex + 1) % searchState.searchOptions.length;
                        const nextTerm = searchState.searchOptions[searchState.currentIndex];
                        
                        if (nextTerm && nextTerm !== currentSearchTerm) {
                            const retrySuccess = await webViewer.searchAndHighlight(nextTerm);
                            if (retrySuccess) {
                                searchState.currentIndex = (searchState.currentIndex + 1) % searchState.searchOptions.length;
                            }
                        }
                    }
                } catch (error) {
                    console.error('Search failed:', error);
                }
            },
            onWebViewerLoaded(pdfInstance) {
                
                // Store the PDF instance reference for easier access
                if (this.$refs.webViewer) {
                    this.$refs.webViewer.pdfInstance = pdfInstance;
                }
                
                // Trigger validation now that WebViewer is ready
                if (this.editedDocument.aidescription) {
                    setTimeout(() => this.validateDataPoints(), 500);
                }
            },
        },
        components: {
            'k-dialog': Dialog,
            'k-dialog-actions': DialogActionsBar,
            'k-textarea': TextArea,
            'k-button': Button,
            'k-dropdown': DropDownList,
            'k-checkbox': Checkbox,
            'loader': Loader,
            'k-grid': Grid,
            'k-grid-toolbar': GridToolbar,
            'k-input': Input,
            'k-multiselect': MultiSelect,
            'k-notification': Notification,
            'k-notification-group': NotificationGroup,
            'k-fade': Fade,
            'k-panelbar': PanelBar,
             WebViewer
        },
    }
</script>

<style >
    @import '/src/assets/css/editConditionDialog.css';
    @import '/src/assets/css/editDocumentDialog.css';
  .custom-title i {
    font-size: 24px;
    line-height:1.3em;
  }

  .custom-title span {
    font-size: 18px;
    line-height: 1.3em;
  }
  .document-title {
    display: flex;
  }
 .confirm-split-dialog .k-window.k-dialog
   {
    border: 1px solid var(--secondary-ltr);
    border-radius: 8px;
    overflow: hidden;
    width: unset;
    min-width:unset!important;
    max-width: 97%;
    height: unset!important;
  }

  .document-title .k-dropdownlist {
    width: 500px;
  }
  .file-name {
    margin-top: 15px;
    display: flex;
  }
  .file-name-input{
      width:500px;
  }
  .file-info-wrapper{
      padding:15px;
  }
  .ai-summary-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .ai-summary-title {
    font-weight: 600;
    font-size: 16px;
    color: #374151;
  }

  .ai-summary-controls {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .regenerate-btn {
    padding: 4px 8px !important;
    font-size: 11px !important;
    border-radius: 4px !important;
    background: linear-gradient(135deg, #398b87, #2d6a66) !important;
    border: none !important;
    color: white !important;
    transition: all 0.2s ease !important;
    display: flex !important;
    align-items: center !important;
    gap: 6px !important;
    height: 24px !important;
    min-width: auto !important;
  }
  .regenerate-btn:hover:not([disabled]) {
  background: linear-gradient(135deg, #2d6a66, #1f4a47) !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(57, 139, 135, 0.3) !important;
  }
  .regenerate-btn:disabled {
    opacity: 0.6 !important;
    cursor: not-allowed !important;
    transform: none !important;
  }

    .regenerate-btn .k-icon {
      font-size: 12px;
      transform: translate(-2px, -1px);
    }

    .regenerate-btn .k-i-loading {
      animation: spin 1s linear infinite;
    }

    .regenerate-btn .btn-text {
      white-space: nowrap;
      font-weight: 500;
    }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }

  .ai-summary-label {
    position: relative;
    display: block;
    margin: 10px 0;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    flex-shrink: 0;
  }

  .ai-summary-content {
    position: relative;
    min-height: 150px;
    padding: 12px;
    border: 1.5px solid #e0e6ed;
    border-radius: 6px;
    background-color: #f8fafc;
    font-family: inherit;
    font-size: 13px;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    overflow-y: auto;
    max-height: 300px;
    transition: border-color 0.2s ease;
    width: 100%;
    box-sizing: border-box;
  }

    .ai-summary-content:hover {
      border-color: #cbd5e1;
    }

    .ai-summary-content.loading {
      overflow: hidden; /* Prevent scrolling during loading */
    }

      .ai-summary-content.loading > *:not(.loading-overlay) {
        filter: blur(2px);
        opacity: 0.5;
        transition: filter 0.3s ease, opacity 0.3s ease;
      }

    .ai-summary-content strong {
      font-weight: 600;
      color: #374151;
    }

    .ai-summary-content em {
      font-style: italic;
      color: #6b7280;
    }

    .ai-summary-content ul {
      margin: 12px 0;
      padding-left: 24px;
    }

    .ai-summary-content li {
      margin: 6px 0; 
    }

    .ai-summary-content p {
      margin: 12px 0;
    }

    .ai-summary-content.loading::before {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(248, 250, 252, 0.8);
      backdrop-filter: blur(1px);
      z-index: 1;
      pointer-events: none;
    }

    .ai-summary-content.loading::after {
      content: "✨";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      font-size: 32px;
      z-index: 2;
      animation: sparkle-pulse 1.5s ease-in-out infinite;
      pointer-events: none;
    }
    .ai-summary-content.loading[data-chat="true"]::after {
      content: "💬";
      animation: chat-pulse 1.5s ease-in-out infinite;
    }

  .regenerate-btn .btn-text {
    white-space: nowrap;
    font-weight: 500;
    min-width: fit-content;
  }

  /* Sparkle animation */
  @keyframes sparkle-pulse {
    0%, 100% {
      opacity: 1;
      transform: translate(-50%, -50%) scale(1);
    }

    50% {
      opacity: 0.7;
      transform: translate(-50%, -50%) scale(1.1);
    }
  }

  /* Document Chat Styling */
  .document-chat-container {
    margin: 5px 0 10px 0;
    width: 100%;
    flex-shrink: 0;
  }

  .title--document-detail + .document-chat-container {
    margin-top: 20px;
  }

  .chat-input-wrapper {
    position: relative;
    width: 100%;
  }

  .chat-input-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    z-index: 10;
  }

  .chat-loader {
    font-size: 16px;
    color: #398b87;
    animation: spin 1s linear infinite;
  }

  .ai-icon {
    font-size: 16px;
    opacity: 0.6;
    transition: opacity 0.2s ease;
  }

  .chat-input-wrapper:hover .ai-icon {
    opacity: 0.8;
  }

  .document-chat-input.k-textarea:focus + .chat-input-icon .ai-icon {
    opacity: 1;
  }

  .document-chat-input.k-textarea {
    width: 100% !important;
    height: 40px !important;
    padding: 8px 40px 8px 8px !important;
    border: 1.5px solid #e0e6ed !important;
    border-radius: 6px !important;
    background-color: #f8fafc !important;
    font-family: inherit !important;
    font-size: 14px !important;
    line-height: 1.5 !important;
    resize: none !important;
    min-height: 40px !important;
    max-height: 120px !important;
    transition: border-color 0.2s ease !important;
    box-sizing: border-box !important;
    overflow: hidden !important;
  }

  .document-chat-input.k-textarea .k-input-inner {
    overflow: hidden !important;
  }

  .document-chat-input.k-textarea:hover {
    border-color: #cbd5e1 !important;
  }

    .document-chat-input.k-textarea:focus {
      border-color: #398b87 !important;
      outline: none !important;
      box-shadow: 0 0 0 3px rgba(57, 139, 135, 0.1) !important;
    }
    .document-chat-input.k-textarea:disabled {
      opacity: 0.6 !important;
      cursor: not-allowed !important;
      background-color: #f3f4f6 !important;
    }
    .chat-error {
      background-color: #fee2e2;
      color: #dc2626;
      padding: 8px 12px;
      margin: 8px 0;
      border-radius: 4px;
      border: 1px solid #fecaca;
      font-size: 14px;
      display: flex;
      align-items: center;
    }
    .chat-error .k-icon {
      margin-right: 6px;
    }
    .back-to-summary-btn {
      padding: 4px 8px !important;
      font-size: 11px !important;
      border-radius: 4px !important;
      background: #f3f4f6 !important;
      border: 1px solid #d1d5db !important;
      color: #6b7280 !important;
      transition: all 0.2s ease !important;
      display: flex !important;
      align-items: center !important;
      gap: 6px !important;
      height: 24px !important;
      min-width: auto !important;
    }
      .back-to-summary-btn:hover {
        background: #e5e7eb !important;
        color: #374151 !important;
      }

      .back-to-summary-btn .k-icon {
        font-size: 12px;
      }

      .back-to-summary-btn .btn-text {
        white-space: nowrap;
        font-weight: 500;
      }

  /* Ensure proper spacing for all elements after AI elements */
  .edit-document-dialog .document-chat-container + label {
    margin-top: 20px !important;
  }

  .edit-document-dialog .document-chat-container + label .k-textarea {
    margin-bottom: 20px !important;
  }


  /* Fix the main section to use proper layout */
  .edit-document-dialog .edit-condition__section-detail {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important; /* Changed so it fills the parent container */
    max-height: calc(100vh - 150px); /* Adjust based on your dialog's header/footer height */
    overflow-y: auto !important; /* Changed to allow vertical scrolling */
    overflow-x: hidden !important;
    flex: 1 !important;
    padding-right: 8px; /* Give the scrollbar some breathing room */
  }

  /* Fix the detail-tracking container to stack properly */
  .edit-document-dialog .edit-condition__detail-tracking {
    display: flex !important;
    flex-direction: column !important;
    height: auto !important;
  }

  /* Fix document condition container to be properly sized and scrollable */
  .edit-document-dialog .document-condition__container {
    position: relative !important;
    height: auto !important;
    min-height: 50px !important;
    max-height: 150px !important;
    margin-bottom: 20px !important;
    overflow-y: auto !important;
    flex-shrink: 0 !important;
  }


  /* Fix select-condition__wrapper to move with its parent */
  .edit-document-dialog .select-condition__wrapper {
    position: relative !important;
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  /* Appraisal Notices */
  .appraisal-distance-notice,
  .appraisal-condition-notice,
  .appraisal-adjustment-notice {
    background-color: #fef3c7;
    border: 1px solid #f59e0b;
    border-left: 4px solid #f59e0b;
    color: #92400e;
    padding: 12px 16px;
    margin-top: 12px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
    font-weight: 500;
    animation: slideIn 0.3s ease-out;
  }

  .appraisal-distance-notice .k-icon,
  .appraisal-condition-notice .k-icon,
  .appraisal-adjustment-notice .k-icon {
    color: #f59e0b;
    font-size: 18px;
    flex-shrink: 0;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
</style>
